import { FC, useEffect, useRef } from 'react';

import { useTourContext } from 'tours/TourProvider';

import { DocumentLibraryUpload } from './components/DocumentLibraryUpload';
import { InterestRates } from './components/InterestRates';
import { MarketColor } from './components/MarketColor';
import { NotificationsSection } from './components/NotificationsSection';
import { PortfolioOverview } from './components/PortfolioOverview';
import { SaleBlotter } from './components/SaleBlotter';
import { LatestNews } from './News';

const TOUR_START_DELAY_MS = 300;
const MAX_RETRY_ATTEMPTS = 20;
const RETRY_DELAY_MS = 150;

const checkTourElementsReady = (): boolean => {
  const firstElement = document.querySelector('[data-tour="home-nav"]');
  return firstElement !== null;
};

export const HomePage: FC = () => {
  const { startTour, tourDataLoading } = useTourContext();
  const hasStartedTours = useRef(false);
  const retryAttemptsRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTourRef = useRef(startTour);
  const previousLoadingRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  useEffect(() => {
    const wasLoading = previousLoadingRef.current;
    previousLoadingRef.current = tourDataLoading;

    if (hasStartedTours.current) {
      return;
    }

    if (tourDataLoading) {
      return;
    }

    const attemptStartTour = () => {
      if (hasStartedTours.current || tourDataLoading) {
        return;
      }

      if (checkTourElementsReady()) {
        retryAttemptsRef.current = 0;
        timeoutRef.current = setTimeout(() => {
          if (hasStartedTours.current || tourDataLoading) {
            return;
          }
          hasStartedTours.current = true;
          startTourRef.current('home-page');
          timeoutRef.current = null;
        }, TOUR_START_DELAY_MS);
      } else if (retryAttemptsRef.current < MAX_RETRY_ATTEMPTS) {
        retryAttemptsRef.current += 1;
        timeoutRef.current = setTimeout(attemptStartTour, RETRY_DELAY_MS);
      } else {
        console.warn('[Tour] Home page tour elements not found after max retries');
      }
    };

    if (wasLoading === undefined || wasLoading === true) {
      attemptStartTour();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [tourDataLoading]);
  return (
    <div
      className="mt-3 h-full rounded-lg bg-background-surface p-3 light:bg-white"
      style={{ gridArea: 'page' }}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-[24px]">
        <div>
          <div className="overflow-x-hidden">
            <MarketColor />
          </div>
          <div className="mt-[16px] overflow-x-hidden">
            <InterestRates />
          </div>
        </div>
        <div>
          <div>
            <PortfolioOverview />
          </div>
          <div className="mt-[24px] overflow-hidden">
            <SaleBlotter />
          </div>
          <div className="mt-[24px] h-[460px] overflow-hidden">
            <NotificationsSection />
          </div>
        </div>
        <div>
          <section data-testid="market-news-section" className="h-[360px] overflow-hidden">
            <LatestNews />
          </section>
          <div className="mt-[24px] min-h-[360px]">
            <DocumentLibraryUpload />
          </div>
        </div>
      </div>
    </div>
  );
};
